import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight, FaExclamationTriangle, FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import { getOptimizedImageUrl, getMediaUrl, formatTime, getOptimizedVideoUrl, detectIOSDevice } from '../../utils/mediaUtils';

/**
 * Component for displaying a lightbox for media items
 */
const Lightbox = ({ 
  lightboxOpen, 
  currentMedia, 
  closeLightbox, 
  navigateLightbox, 
  handleTouchStart, 
  handleTouchEnd,
  handleLightboxImageLoad,
  handleLightboxImageError,
  retryLightbox,
  // Video related props
  isPlaying,
  currentTimes,
  durations,
  isMuted,
  videoRefs,
  progressRefs,
  toggleVideo,
  toggleMute,
  handleTimeUpdate,
  handleMetadataLoaded,
  handleSeek,
  resetVideoState
}) => {
  // Detect iOS for special handling
  const [isIOS, setIsIOS] = useState(false);
  const [videoLoading, setVideoLoading] = useState({});
  // Add volume state
  const [volumes, setVolumes] = useState({});
  
  // Detect iOS devices more accurately
  useEffect(() => {
    const checkIOSDevice = () => {
      const isIOSDevice = detectIOSDevice();
      setIsIOS(isIOSDevice);
    };
    
    checkIOSDevice();
  }, []);
  
  const getVideoMuted = useCallback((id) => isMuted[id] === true, [isMuted]);

  // Function to optimize video loading for iOS
  const optimizeVideoForIOS = useCallback((videoElement, mediaId) => {
    if (!videoElement || !isIOS) return;

    const restoreMuted = getVideoMuted(mediaId);

    videoElement.setAttribute('playsinline', true);
    videoElement.setAttribute('webkit-playsinline', true);
    videoElement.style.transform = 'translateZ(0)';

    if (videoElement.videoHeight === 0) {
      videoElement.setAttribute('x-webkit-airplay', 'allow');
      videoElement.loop = false;
      videoElement.muted = true;
      videoElement.load();

      try {
        const playPromise = videoElement.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            setTimeout(() => {
              videoElement.pause();
              videoElement.currentTime = 0;
              videoElement.muted = restoreMuted;
              if (!restoreMuted) {
                videoElement.volume = volumes[mediaId] ?? 1;
              }
            }, 50);
          }).catch(() => {
            videoElement.pause();
            videoElement.muted = restoreMuted;
          });
        }
      } catch (e) {
        videoElement.muted = restoreMuted;
      }
    } else {
      videoElement.muted = restoreMuted;
      if (!restoreMuted) {
        videoElement.volume = volumes[mediaId] ?? 1;
      }
    }
  }, [getVideoMuted, isIOS, volumes]);
  
  // Effect to optimize videos when they load
  useEffect(() => {
    if (currentMedia?.mediaType === 'video' && videoRefs.current[currentMedia._id]) {
      optimizeVideoForIOS(videoRefs.current[currentMedia._id], currentMedia._id);
    }
  }, [currentMedia, optimizeVideoForIOS]);

  // Keep the lightbox video element in sync with mute/volume state
  useEffect(() => {
    if (!lightboxOpen || currentMedia?.mediaType !== 'video') return;

    const videoElement = videoRefs.current[currentMedia._id];
    if (!videoElement) return;

    const shouldMute = getVideoMuted(currentMedia._id);
    videoElement.muted = shouldMute;
    videoElement.volume = shouldMute ? 0 : (volumes[currentMedia._id] ?? 1);
  }, [lightboxOpen, currentMedia, getVideoMuted, volumes, isMuted, videoRefs]);
  
  // Effect to force iOS videos to preload correctly
  useEffect(() => {
    if (currentMedia && currentMedia.mediaType === 'video' && isIOS) {
      const videoElement = videoRefs.current[currentMedia._id];
      if (videoElement) {
        // Set a short timeout to allow the DOM to update
        setTimeout(() => {
          // Set a very small current time to force frame loading
          videoElement.currentTime = 0.01;
          
          // Create a promise to track when the video has loaded enough data
          const loadPromise = new Promise((resolve) => {
            const handleDataLoaded = () => {
              videoElement.removeEventListener('loadeddata', handleDataLoaded);
              resolve();
            };
            
            videoElement.addEventListener('loadeddata', handleDataLoaded);
            
            // Also resolve after a timeout in case event never fires
            setTimeout(resolve, 1000);
          });
          
          // After the promise resolves, make sure we're showing a frame
          loadPromise.then(() => {
            videoElement.pause();
            
            // Ensure video controls are shown
            if (progressRefs.current[currentMedia._id]) {
              progressRefs.current[currentMedia._id].style.width = '0%';
            }
          });
        }, 100);
      }
    }
  }, [currentMedia, isIOS]);
  
  // Handle touch events for iOS video controls
  const handleVideoTouchStart = useCallback((e) => {
    if (isIOS && currentMedia?.mediaType === 'video') {
      const wrapper = e.currentTarget;
      wrapper.classList.add('touching');
      wrapper.classList.add('controls-visible');
    }
  }, [isIOS, currentMedia]);

  const handleVideoTouchEnd = useCallback((e) => {
    if (isIOS && currentMedia?.mediaType === 'video') {
      const wrapper = e.currentTarget;
      wrapper.classList.remove('touching');
      // Keep controls visible for 3 seconds after touch ends
      setTimeout(() => {
        if (wrapper) {
          wrapper.classList.remove('controls-visible');
        }
      }, 3000);
    }
  }, [isIOS, currentMedia]);

  // Handle control interaction to keep controls visible
  const handleControlInteraction = useCallback((e) => {
    if (isIOS && currentMedia?.mediaType === 'video') {
      const wrapper = e.currentTarget.closest('.lightbox-video-wrapper');
      if (wrapper) {
        wrapper.classList.add('controls-visible');
        // Reset the timeout when interacting with controls
        clearTimeout(wrapper.controlsTimeout);
        wrapper.controlsTimeout = setTimeout(() => {
          if (wrapper) {
            wrapper.classList.remove('controls-visible');
          }
        }, 3000);
      }
    }
  }, [isIOS, currentMedia]);
  
  // Handle volume change
  const handleVolumeChange = useCallback((id, value) => {
    const videoElement = videoRefs.current[id];
    if (!videoElement) return;
    
    // Update volume state
    setVolumes(prev => ({ ...prev, [id]: value }));
    
    // Set video volume
    videoElement.volume = value;
    
    // Update mute state based on volume
    if (value === 0 && !isMuted[id]) {
      toggleMute(id, 'video');
    } else if (value > 0 && isMuted[id]) {
      toggleMute(id, 'video');
    }
  }, [videoRefs, isMuted, toggleMute]);

  // Handle mute toggle with volume sync
  const handleMuteToggle = useCallback((id) => {
    const videoElement = videoRefs.current[id];
    if (!videoElement) return;
    
    if (isMuted[id]) {
      // Unmute and restore previous volume or default to 1
      const previousVolume = volumes[id] || 1;
      videoElement.volume = previousVolume;
      setVolumes(prev => ({ ...prev, [id]: previousVolume }));
    } else {
      // Store current volume before muting
      const currentVolume = videoElement.volume;
      setVolumes(prev => ({ ...prev, [id]: currentVolume }));
      videoElement.volume = 0;
    }
    
    toggleMute(id, 'video');
  }, [videoRefs, isMuted, volumes, toggleMute]);
  
  if (!lightboxOpen || !currentMedia) return null;

  const renderLightboxContent = () => {
    if (!currentMedia) return null;
    
    switch (currentMedia.mediaType) {
      case 'image': {
        const mediaUrl = getOptimizedImageUrl(currentMedia.filePath, 'lightbox');
        
        return (
          <div className="lightbox-image-container">
            {currentMedia.mediaArray && currentMedia.mediaArray.length > 1 && (
              <button 
                className="lightbox-nav lightbox-prev" 
                onClick={(e) => {
                  e.stopPropagation();
                  navigateLightbox('prev');
                }}
                aria-label="Previous image"
              >
                <FaChevronLeft />
              </button>
            )}
            <div className="lightbox-image-wrapper">
              {currentMedia.error ? (
                <div className="lightbox-image-error">
                  <FaExclamationTriangle />
                  <p>Failed to load image</p>
                  <button 
                    className="lightbox-retry-button"
                    onClick={() => retryLightbox && retryLightbox()}
                    aria-label="Retry loading image"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <img 
                  src={mediaUrl} 
                  alt={currentMedia.title || 'Gallery image'} 
                  loading="eager" // Force immediate loading
                  decoding="async" // Use async decoding for better performance
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                  style={{ 
                    visibility: currentMedia.loading ? 'hidden' : 'visible',
                    maxHeight: '80vh',
                    maxWidth: '100%'
                  }}
                  onLoad={() => handleLightboxImageLoad(currentMedia._id)}
                  onError={() => handleLightboxImageError()}
                />
              )}
              {currentMedia.loading && (
                <div className="lightbox-loading">
                  <div className="loading-spinner"></div>
                </div>
              )}
            </div>
            {currentMedia.mediaArray && currentMedia.mediaArray.length > 1 && (
              <button 
                className="lightbox-nav lightbox-next" 
                onClick={(e) => {
                  e.stopPropagation();
                  navigateLightbox('next');
                }}
                aria-label="Next image"
              >
                <FaChevronRight />
              </button>
            )}
          </div>
        );
      }
      case 'video': {
        const mediaUrl = getMediaUrl(currentMedia.filePath);
        const videoUrls = getOptimizedVideoUrl(currentMedia.filePath, currentMedia.fileType, isIOS);
        const id = currentMedia._id;
        const thumbnailUrl = currentMedia.thumbnailPath ? getOptimizedImageUrl(currentMedia.thumbnailPath, 'lightbox') : undefined;
        const isVideoLoading = videoLoading[id] !== false;
        
        return (
          <div className="lightbox-video-container">
            {currentMedia.mediaArray && currentMedia.mediaArray.length > 1 && (
              <button 
                className="lightbox-nav lightbox-prev" 
                onClick={(e) => {
                  e.stopPropagation();
                  // Pause current video before navigating
                  if (isPlaying[id]) {
                    toggleVideo(id);
                  }
                  // Reset video state completely
                  resetVideoState(id);
                  navigateLightbox('prev');
                }}
                aria-label="Previous video"
              >
                <FaChevronLeft />
              </button>
            )}
            <div 
              className="lightbox-video-wrapper"
              onTouchStart={handleVideoTouchStart}
              onTouchEnd={handleVideoTouchEnd}
            >
              <video 
                key={`lightbox-video-${id}`} 
                ref={el => videoRefs.current[id] = el}
                preload={isIOS ? "auto" : "metadata"}
                poster={thumbnailUrl}
                onClick={() => toggleVideo(id)}
                onTimeUpdate={() => handleTimeUpdate(id, videoRefs.current[id])}
                onLoadStart={() => setVideoLoading(prev => ({ ...prev, [id]: true }))}
                onCanPlay={() => setVideoLoading(prev => ({ ...prev, [id]: false }))}
                onLoadedData={() => setVideoLoading(prev => ({ ...prev, [id]: false }))}
                onLoadedMetadata={(e) => {
                  // Initialize progress bar when metadata is loaded
                  if (progressRefs.current[id]) {
                    progressRefs.current[id].style.width = '0%';
                  }
                  // Update duration via the shared playback state (not a direct
                  // mutation of the prop, which wouldn't trigger a re-render).
                  if (handleMetadataLoaded && e.target.duration && !isNaN(e.target.duration)) {
                    handleMetadataLoaded(id, e.target);
                  }
                  
                  // Mark video as loaded
                  setVideoLoading(prev => ({ ...prev, [id]: false }));
                  
                  // For iOS, we need to manually start loading the video
                  if (isIOS) {
                    const video = videoRefs.current[id];
                    if (video) {
                      // Force video to start loading frames
                      video.currentTime = 0.1;
                      // Pause immediately to show a preview frame
                      video.pause();
                    }
                  }
                }}
                onEnded={() => {
                  // When video ends in lightbox, go to next if available
                  if (currentMedia.mediaArray && currentMedia.mediaArray.length > 1) {
                    resetVideoState(id);
                    navigateLightbox('next');
                  }
                }}
                controls={false}
                playsInline
                muted={getVideoMuted(id)}
                className={`lightbox-video ${isIOS ? 'ios-video' : ''}`}
                style={{ objectFit: 'contain' }}
              >
                <source src={videoUrls.primary} type={isIOS ? "video/mp4" : currentMedia.fileType} />
                <source src={videoUrls.mp4} type="video/mp4" />
                <source src={videoUrls.webm} type="video/webm" />
                Your browser does not support the video tag or this video format.
              </video>
              
              {/* Loading indicator for iOS videos */}
              {isIOS && isVideoLoading && (
                <div className="lightbox-video-loading">
                  <div className="loading-spinner"></div>
                  <span>Loading video preview...</span>
                </div>
              )}
              
              {!isPlaying[id] && (
                <div className="lightbox-video-overlay">
                  <button 
                    className="lightbox-video-play-button"
                    onClick={() => toggleVideo(id)}
                    aria-label="Play"
                    type="button"
                  >
                    <FaPlay />
                  </button>
                </div>
              )}
              
              <div 
                className="lightbox-video-controls" 
                onClick={e => {
                  e.stopPropagation();
                  handleControlInteraction(e);
                }}
              >
                <div className="lightbox-video-controls-primary">
                  <button 
                    className="lightbox-video-play-button-small"
                    onClick={() => toggleVideo(id)}
                    aria-label={isPlaying[id] ? "Pause" : "Play"}
                    type="button"
                  >
                    {isPlaying[id] ? <FaPause /> : <FaPlay />}
                  </button>
                  <div className="lightbox-video-time">
                    <span className="current-time">{formatTime(currentTimes[id] || 0)}</span>
                    <span className="duration">{formatTime(durations[id] || 0)}</span>
                  </div>
                </div>
                <div 
                  className="lightbox-video-progress-container"
                  onClick={(e) => handleSeek(e, id, 'video')}
                >
                  <div className="lightbox-video-progress-bg">
                    <div 
                      className="lightbox-video-progress-bar" 
                      ref={el => progressRefs.current[id] = el}
                      style={{ width: '0%' }}
                    ></div>
                  </div>
                </div>
                <div className="lightbox-video-controls-secondary">
                  <button 
                    className="lightbox-video-mute-button"
                    onClick={() => handleMuteToggle(id)}
                    aria-label={isMuted[id] ? "Unmute" : "Mute"}
                    type="button"
                  >
                    {isMuted[id] ? <FaVolumeMute /> : <FaVolumeUp />}
                  </button>
                  
                  {/* Add volume slider */}
                  <div className="lightbox-video-volume-control">
                    <div className="lightbox-video-volume-slider-wrapper">
                      <div 
                        className="lightbox-video-volume-level-bg" 
                        style={{ width: `${isMuted[id] ? 0 : (volumes[id] || 1) * 100}%` }}
                      />
                      <input
                        type="range"
                        className="lightbox-video-volume-slider"
                        min="0"
                        max="1"
                        step="0.01"
                        value={isMuted[id] ? 0 : (volumes[id] || 1)}
                        onChange={(e) => handleVolumeChange(id, parseFloat(e.target.value))}
                        aria-label="Volume"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {currentMedia.mediaArray && currentMedia.mediaArray.length > 1 && (
              <button 
                className="lightbox-nav lightbox-next" 
                onClick={(e) => {
                  e.stopPropagation();
                  // Pause current video before navigating
                  if (isPlaying[id]) {
                    toggleVideo(id);
                  }
                  // Reset video state completely
                  resetVideoState(id);
                  navigateLightbox('next');
                }}
                aria-label="Next video"
              >
                <FaChevronRight />
              </button>
            )}
          </div>
        );
      }
      
      case 'audio': {
        const mediaUrl = getMediaUrl(currentMedia.filePath);
        
        return (
          <div className="lightbox-audio-container">
            <div className="audio-player-wrapper">
              <div className="audio-title">{currentMedia.title}</div>
              <audio controls autoPlay>
                <source src={mediaUrl} type={currentMedia.fileType} />
                Your browser does not support the audio tag.
              </audio>
              {currentMedia.description && (
                <div className="audio-description">{currentMedia.description}</div>
              )}
            </div>
          </div>
        );
      }
      
      default:
        return (
          <div className="lightbox-unsupported">
            <p>This media type is not supported in lightbox view.</p>
          </div>
        );
    }
  };

  return (
    <div className="lightbox" onClick={() => {
      // Reset video state if the current media is a video
      if (currentMedia && currentMedia.mediaType === 'video') {
        resetVideoState(currentMedia._id);
      }
      closeLightbox();
    }}>
      <div className="lightbox-content" onClick={e => e.stopPropagation()}>
        <button className="lightbox-close" onClick={() => {
          // Reset video state if the current media is a video
          if (currentMedia && currentMedia.mediaType === 'video') {
            resetVideoState(currentMedia._id);
          }
          closeLightbox();
        }} aria-label="Close">
          <FaTimes />
        </button>
        {renderLightboxContent()}
        <div className="lightbox-caption">
          <h3>{currentMedia.title}</h3>
          {currentMedia.description && <p>{currentMedia.description}</p>}
        </div>
      </div>
    </div>
  );
};

export default Lightbox; 